import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { LoadingState } from '@/components/ui/loading-state';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle, Download, ExternalLink } from 'lucide-react';
import { subscribeToNewsletterDirectly } from '@/services/emailSubscriptionService';
import { parseFullName } from '@/lib/nameUtils';

export default function EmailCallback() {
  const [status, setStatus] = useState<'loading' | 'success' | 'app-redirect' | 'web-fallback' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');

  useEffect(() => {
    handleEmailVerification();
  }, []);

  const handleEmailVerification = async () => {
    try {
      console.log('🌐 EmailCallback: Starting email verification process');
      console.log('🌐 Current URL:', window.location.href);
      console.log('🌐 Hash fragment:', window.location.hash);

      // Let Supabase process the URL fragments automatically
      // This will handle the session creation from the hash fragments
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('🌐 Email verification error:', error);
        setErrorMessage(error.message || 'Email verification failed. Please try again.');
        setStatus('error');
        return;
      }

      if (session?.access_token && session?.refresh_token) {
        console.log('🌐 Email verification successful for:', session.user?.email);
        setUserEmail(session.user?.email || '');
        
        // Handle newsletter subscription if user opted in
        const newsletterConsent = session.user?.user_metadata?.newsletter_consent;
        if (newsletterConsent) {
          console.log('🌐 User opted for newsletter, subscribing...');
          try {
            const { firstName, lastName } = parseFullName(session.user?.user_metadata?.full_name || '');
            await subscribeToNewsletterDirectly(
              session.user!.email!,
              firstName,
              lastName
            );
            console.log('🌐 Newsletter subscription successful');
          } catch (error) {
            console.error('🌐 Newsletter subscription failed:', error);
            // Don't fail the auth flow for newsletter issues
          }
        }
        
        // Try to redirect to native app with session tokens
        const appUrl = `thematmonster-auth://auth/callback?access_token=${encodeURIComponent(session.access_token)}&refresh_token=${encodeURIComponent(session.refresh_token)}&token_type=${encodeURIComponent(session.token_type || 'bearer')}&expires_in=${session.expires_in || 3600}`;
        
        console.log('🌐 Attempting to redirect to native app...');
        setStatus('app-redirect');
        
        // Attempt to open native app
        window.location.href = appUrl;
        
        // Fallback to web after delay if app doesn't open
        setTimeout(() => {
          console.log('🌐 App redirect timeout, showing web fallback');
          setStatus('web-fallback');
        }, 3000);
        
      } else {
        console.error('🌐 No session tokens found after email verification');
        setErrorMessage('Email verification completed but session creation failed. Please try signing in manually.');
        setStatus('error');
      }
    } catch (error) {
      console.error('🌐 Email verification exception:', error);
      setErrorMessage('Email verification failed. Please try again.');
      setStatus('error');
    }
  };

  const handleRetry = () => {
    setStatus('loading');
    setErrorMessage('');
    handleEmailVerification();
  };

  const handleContinueOnWeb = () => {
    // Redirect to the main app dashboard or login page
    window.location.href = '/dashboard';
  };

  const handleDownloadApp = () => {
    // Redirect to app store or download page
    window.open('https://apps.apple.com/app/the-mat-monster/id6736678804', '_blank');
  };

  if (status === 'loading') {
    return (
      <LoadingState
        title="Confirming your email"
        description="Please wait while we verify your account..."
        fullScreen={true}
      />
    );
  }

  if (status === 'app-redirect') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <div>
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100">
              <ExternalLink className="h-8 w-8 text-blue-600" />
            </div>
            <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900">
              Opening The Mat Monster App
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Your email has been confirmed! Redirecting you to the app...
            </p>
          </div>
          <div className="space-y-4">
            <p className="text-xs text-gray-500">
              If the app doesn't open automatically, you can:
            </p>
            <div className="flex flex-col space-y-2">
              <Button 
                onClick={handleDownloadApp}
                variant="outline"
                className="w-full"
              >
                <Download className="h-4 w-4 mr-2" />
                Download The Mat Monster App
              </Button>
              <Button 
                onClick={handleContinueOnWeb}
                variant="ghost"
                className="w-full"
              >
                Continue on Web
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'web-fallback') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <div>
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900">
              Email Confirmed!
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Your account has been successfully verified{userEmail && ` for ${userEmail}`}.
            </p>
          </div>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Get the full experience with our mobile app:
            </p>
            <div className="flex flex-col space-y-2">
              <Button 
                onClick={handleDownloadApp}
                className="w-full"
              >
                <Download className="h-4 w-4 mr-2" />
                Download The Mat Monster App
              </Button>
              <Button 
                onClick={handleContinueOnWeb}
                variant="outline"
                className="w-full"
              >
                Continue on Web
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <div>
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900">
              Email Confirmed!
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Your account has been successfully verified. You can now sign in.
            </p>
          </div>
          <Button 
            onClick={handleContinueOnWeb}
            className="w-full"
          >
            Continue to Login
          </Button>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 text-center">
        <div>
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900">
            Confirmation Failed
          </h2>
        </div>
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {errorMessage}
          </AlertDescription>
        </Alert>
        <div className="flex flex-col space-y-2">
          <Button 
            onClick={handleRetry}
            className="w-full"
          >
            Try Again
          </Button>
          <Button 
            onClick={() => window.location.href = '/auth/signup'}
            variant="outline"
            className="w-full"
          >
            Back to Sign Up
          </Button>
        </div>
      </div>
    </div>
  );
}
